/**
 * Shared per-type field builder for asset cards.
 *
 * Two surfaces render the same metadata grid below the asset name:
 *   - The wizard's `AssetSummaryCard` (collapsible block header on
 *     `asset-activities`).
 *   - The Manage Assets list's `AssetCard` (and the asset detail
 *     page's read-mode summary).
 *
 * Pulling the field-set + display layout into one helper keeps the two
 * surfaces aligned automatically — adding a new per-type field once
 * (e.g. PUE on DataCentre) shows up in both without two parallel
 * edits.
 *
 * Inputs are duck-typed (`Partial<CreateAssetRequest>`) so this works
 * for both the wizard's `CreateAssetRequest` shape and the API's
 * `EmissionAsset` shape — both carry the same per-type fields.
 */

import {
  MapPin,
  FileText,
  Zap,
  Ruler,
  Users,
  Hash,
  Gauge,
  Server,
  Clock,
  Car as CarIcon,
  type LucideIcon,
} from 'lucide-react';
import { COUNTRY_CODES } from '@g17eco/emissions-config';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { AssetType, type CreateAssetRequest } from '../../types/emissions';
import { VEHICLE_TYPE_LABELS } from '../form/types';

export interface SummaryFieldData {
  Icon: LucideIcon;
  label: string;
  value: string;
}

const countryName = (code?: string): string => {
  if (!code) {
    return '';
  }
  return COUNTRY_CODES.find(([c]) => c === code)?.[1] ?? code;
};

/**
 * Build the metadata field list for an asset. Order is stable across
 * the two surfaces; per-type fields slot in based on `assetType`.
 *
 * Accepts a duck-typed shape so wizard (`CreateAssetRequest`) and
 * API (`EmissionAsset`) records both work — they share every field
 * this helper reads.
 */
export const buildAssetSummaryFields = (
  asset: Partial<CreateAssetRequest>,
): SummaryFieldData[] => {
  const fields: SummaryFieldData[] = [];
  const country = countryName(asset.region);

  if (country) {
    fields.push({ Icon: MapPin, label: 'Country', value: country });
  }
  if (asset.description) {
    fields.push({ Icon: FileText, label: 'Description', value: asset.description });
  }

  if (asset.assetType === AssetType.Facility || asset.assetType === AssetType.ManufacturingFacility) {
    if (asset.floorAreaM2 !== undefined) {
      fields.push({
        Icon: Ruler,
        label: 'Floor Area',
        value: `${asset.floorAreaM2.toLocaleString()} m²`,
      });
    }
    if (asset.employeeCount !== undefined) {
      fields.push({
        Icon: Users,
        label: 'Employees',
        value: asset.employeeCount.toLocaleString(),
      });
    }
  }

  if (asset.assetType === AssetType.Vehicle) {
    if (asset.vehicleType) {
      // Resolve the raw enum value (e.g. `small_car`) to its
      // human-readable label (`Small Car`) — same map the wizard's
      // vehicle-type picker uses.
      const vehicleLabel = VEHICLE_TYPE_LABELS[asset.vehicleType] ?? String(asset.vehicleType);
      fields.push({ Icon: CarIcon, label: 'Vehicle Type', value: vehicleLabel });
    }
    if (asset.identifier) {
      fields.push({ Icon: Hash, label: 'Identifier', value: asset.identifier });
    }
  }

  if (asset.assetType === AssetType.DataCentre) {
    if (asset.pue !== undefined) {
      fields.push({ Icon: Gauge, label: 'PUE', value: String(asset.pue) });
    }
    if (asset.itLoadKw !== undefined) {
      fields.push({
        Icon: Zap,
        label: 'IT Load',
        value: `${asset.itLoadKw.toLocaleString()} kW`,
      });
    }
    if (asset.rackCount !== undefined) {
      fields.push({
        Icon: Server,
        label: 'Rack Count',
        value: asset.rackCount.toLocaleString(),
      });
    }
  }

  if (asset.assetType === AssetType.Equipment) {
    if (asset.operatingHoursPerYear !== undefined) {
      fields.push({
        Icon: Clock,
        label: 'Operating Hours / Year',
        value: `${asset.operatingHoursPerYear.toLocaleString()} hrs`,
      });
    }
    if (asset.ratedCapacityKw !== undefined) {
      fields.push({
        Icon: Zap,
        label: 'Rated Capacity',
        value: `${asset.ratedCapacityKw.toLocaleString()} kW`,
      });
    }
  }

  if (asset.unitCount !== undefined && asset.unitCount > 1) {
    fields.push({
      Icon: Users,
      label: 'Group size',
      value: `${asset.unitCount.toLocaleString()} units`,
    });
  }

  if (asset.customIntensityLabel && asset.customIntensityValue !== undefined) {
    fields.push({
      Icon: Gauge,
      label: asset.customIntensityLabel,
      value: String(asset.customIntensityValue),
    });
  }

  return fields;
};

/**
 * One field row in the metadata grid. Brand-tinted icon + muted label
 * + emphasised value, all on one inline-flex row so they fit several
 * per visual line.
 */
export const SummaryField = ({
  Icon,
  label,
  value,
  iconColor,
}: SummaryFieldData & { iconColor: string }) => (
  <div className='tw:flex tw:items-center tw:gap-2'>
    <Icon className={cn('tw:size-4', iconColor)} />
    <div className='tw:text-xs tw:text-muted-foreground'>{label}</div>
    <div className='tw:text-sm tw:font-medium'>{value}</div>
  </div>
);
